import { useState } from 'react';

const faqs = [
  {
    question: 'Do you make offers on all properties?',
    answer:
      'No. Sometimes if a property does not tick all the boxes for us, we may decline to make an offer. This can happen when a seller has an unrealistic price expectation, or when the property requires a significant amount of work and the numbers simply don\'t stack up for us. While we would love to say yes to every property, sometimes the answer is no — and in those cases we will always politely decline and let you know as quickly as possible.',
  },
  {
    question: 'How is the offer price determined?',
    answer:
      'Our SureBuy agents conduct a thorough, data-driven assessment of your property — factoring in recent comparable sales in your suburb, current market conditions, the property\'s condition, and its potential after value-add improvements. We aim to make a fair, realistic offer that reflects true market value, without the uncertainty of a public campaign.',
  },
  {
    question: 'What types of properties do you buy?',
    answer:
      'We buy residential properties across Melbourne — houses, townhouses, and units. We\'re particularly interested in properties with value-add potential: those needing cosmetic updates, subdivision opportunities, or properties in growth corridors. We also consider distressed sales, deceased estates, divorce settlements, and properties where the vendor needs a fast, certain outcome.',
  },
  {
    question: 'Do I need to prepare or renovate my property before selling?',
    answer:
      'No. One of the biggest advantages of the SureBuy Program is that you sell as-is. No painting, no staging, no cleaning, no open for inspections. We assess the property in its current condition and factor that into our offer — saving you thousands in upfront preparation costs.',
  },
  {
    question: 'How quickly can you move?',
    answer:
      'Very quickly. Our SureBuy agents can visit your property anywhere in Melbourne and provide a written offer within 3–5 business days. The offer is valid for 48 hours. Once accepted, we move to contract swiftly — we are finance-approved and do not drag out the process.',
  },
  {
    question: 'What does it cost me as a seller?',
    answer:
      'Nothing. We charge zero selling fees and zero commission. Compare that to a traditional sale where a real estate agent charges 2.2%–3.5% in commission alone — on a $1M property that\'s $22,000+ out of your pocket, before marketing costs. With us, what we offer is what you receive.',
  },
  {
    question: 'Are you a real estate agent or a buyers agency?',
    answer:
      'Neither. We are a buyers group network — we buy properties for ourselves and our network. We are not representing other buyers, and we are not listing your property for sale. This means we have genuine skin in the game and a real incentive to move quickly and decisively.',
  },
  {
    question: 'I\'m a real estate agent — how does partnering with you work?',
    answer:
      'Simple. Share your stock with us — off-market, on-market, distressed, or confidential — and we\'ll assess it against our buying criteria. If it fits, we can move fast with a clean offer. We don\'t charge you any fees, we\'re finance-approved, and we won\'t drag out negotiations. You get a reliable buyer who closes quickly, and your vendor gets certainty.',
  },
  {
    question: 'What areas of Melbourne do you cover?',
    answer:
      'We cover all of metropolitan Melbourne. Our SureBuy agents will come to you — wherever your property is located within the Melbourne metro area. If you\'re unsure whether your property falls within our coverage, just reach out and we\'ll confirm quickly.',
  },
];

function FAQItem({ question, answer, isOpen, onToggle }: {
  question: string;
  answer: string;
  isOpen: boolean;
  onToggle: () => void;
}) {
  return (
    <div className="border-b border-gray-100 last:border-0">
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between gap-4 py-6 text-left cursor-pointer group"
      >
        <span className={`text-base md:text-lg font-semibold transition-colors ${isOpen ? 'text-teal-700' : 'text-gray-900 group-hover:text-teal-700'}`}>
          {question}
        </span>
        <div className={`w-8 h-8 flex items-center justify-center rounded-full flex-shrink-0 transition-all ${isOpen ? 'bg-teal-700 text-white rotate-45' : 'bg-gray-100 text-gray-500 group-hover:bg-teal-50 group-hover:text-teal-700'}`}>
          <i className="ri-add-line text-lg"></i>
        </div>
      </button>
      <div
        className={`overflow-hidden transition-all duration-300 ease-in-out ${isOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}`}
      >
        <p className="text-gray-600 text-base leading-relaxed pb-6 pr-12">
          {answer}
        </p>
      </div>
    </div>
  );
}

export default function FAQSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  const toggle = (i: number) => setOpenIndex(openIndex === i ? null : i);

  return (
    <section id="faq" className="py-20 md:py-28 bg-white">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
          {/* Left — sticky label */}
          <div className="lg:col-span-4">
            <div className="lg:sticky lg:top-28">
              <span className="inline-block text-xs font-semibold uppercase tracking-widest text-teal-700 mb-4">
                FAQ
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 leading-tight mb-6">
                Questions we get asked a lot
              </h2>
              <p className="text-gray-500 text-base leading-relaxed mb-8">
                Can't find what you're looking for? Reach out directly — we're happy to answer any questions about the process, our criteria, or how we work.
              </p>
              <a
                href="#contact"
                onClick={(e) => {
                  e.preventDefault();
                  document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' });
                }}
                className="inline-flex items-center gap-2 px-6 py-3 bg-teal-700 hover:bg-teal-800 text-white font-semibold rounded-lg transition-colors cursor-pointer whitespace-nowrap text-sm"
              >
                Ask Us Directly
                <i className="ri-arrow-right-line"></i>
              </a>
            </div>
          </div>

          {/* Right — accordion */}
          <div className="lg:col-span-8">
            <div className="divide-y divide-gray-100 border-t border-gray-100">
              {faqs.map((faq, i) => (
                <FAQItem
                  key={i}
                  question={faq.question}
                  answer={faq.answer}
                  isOpen={openIndex === i}
                  onToggle={() => toggle(i)}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
